import type { IssuePriority, IssueStatus, TechnicalIssue } from '../../pages/housekeeping/types/housekeeping';
import { formatDate, t } from '../i18n/langue';

/**
 * Les règles d'affichage de l'écran « Maintenance » du technicien.
 *
 * Libellés lus au rendu dans le dictionnaire mobile (`i18n/fr/maintenance.json`).
 * Ils recopient `pages/housekeeping/constants/maintenanceConfig.ts`, que la
 * frontière interdit d'importer en valeur.
 */

export type OngletMaintenance = 'a_prendre' | 'mes_interventions' | 'terminees';

const ONGLETS: OngletMaintenance[] = ['a_prendre', 'mes_interventions', 'terminees'];

export function ongletsMaintenance(): { valeur: OngletMaintenance; libelle: string }[] {
    return ONGLETS.map((valeur) => ({ valeur, libelle: t(`maintenance.onglets.${valeur}`) }));
}

/**
 * Les filtres serveur de chaque onglet (`HousekeepingTechnicalIssueController::index`).
 * Le tri se fait côté serveur ; « Terminées » arrive par date de résolution.
 */
export function parametresOnglet(onglet: OngletMaintenance): string {
    switch (onglet) {
        case 'a_prendre':
            return 'open=1&unassigned=1';
        case 'mes_interventions':
            return 'open=1&mine=1';
        case 'terminees':
            return 'done=1';
    }
}

export function messageVide(onglet: OngletMaintenance): string {
    return t(`maintenance.vides.${onglet}`);
}

export function libelleStatutPanne(statut: IssueStatus): string {
    return t(`maintenance.statuts.${statut}`);
}

export function libellePriorite(priorite: IssuePriority): string {
    return t(`maintenance.priorites.${priorite}`);
}

/** Les urgences d'abord ; l'ordre du serveur est gardé à priorité égale. */
export function trierPannes(pannes: TechnicalIssue[]): TechnicalIssue[] {
    const rang = (p: TechnicalIssue) => (p.priority === 'critical' ? 0 : p.priority === 'high' ? 1 : 2);
    return [...pannes].sort((a, b) => rang(a) - rang(b));
}

export function lieuPanne(panne: Pick<TechnicalIssue, 'room_name' | 'location_text'>): string {
    if (panne.room_name) return t('commun.chambre', { numero: panne.room_name });
    return panne.location_text || t('maintenance.lieuNonPrecise');
}

/** « à l'instant », « il y a 12 min », « il y a 3 h », « il y a 2 j ». */
export function anciennete(iso: string, maintenant: number = Date.now()): string {
    const minutes = Math.max(0, Math.floor((maintenant - new Date(iso).getTime()) / 60_000));
    if (minutes < 1) return t('maintenance.anciennete.instant');
    if (minutes < 60) return t('maintenance.anciennete.minutes', { n: minutes });
    const heures = Math.floor(minutes / 60);
    if (heures < 24) return t('maintenance.anciennete.heures', { n: heures });
    return t('maintenance.anciennete.jours', { n: Math.floor(heures / 24) });
}

export function heurePanne(iso: string): string {
    return formatDate(iso, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Les gestes de statut proposés au technicien, dans l'ordre d'affichage.
 *
 * Sous-ensemble de `ALLOWED_TRANSITIONS` (web et serveur, identiques) : le
 * technicien commence, met en pause, résout. Clôturer et rouvrir restent au
 * responsable, sur le web.
 */
export interface GesteStatut {
    statut: IssueStatus;
    libelle: string;
}

/** Les libellés sont des clés `maintenance.gestes.*`, traduites au rendu. */
const GESTES: Partial<Record<IssueStatus, GesteStatut[]>> = {
    assigned: [{ statut: 'in_progress', libelle: 'commencer' }],
    in_progress: [
        { statut: 'resolved', libelle: 'resolu' },
        { statut: 'on_hold', libelle: 'enPause' },
    ],
    on_hold: [
        { statut: 'in_progress', libelle: 'reprendre' },
        { statut: 'resolved', libelle: 'resolu' },
    ],
};

export function gestesStatut(statut: IssueStatus): GesteStatut[] {
    return (GESTES[statut] ?? []).map((geste) => ({ ...geste, libelle: t(`maintenance.gestes.${geste.libelle}`) }));
}

export function estTerminee(statut: IssueStatus): boolean {
    return statut === 'resolved' || statut === 'closed';
}
